"use client";

import { usePathname } from "next/navigation";

import { UsageConsentCard } from "@/components/consent/UsageConsentCard";
import { useSessionContext } from "@/components/session/SessionProvider";
import { useOnboardingStateQuery } from "@/hooks/useOnboarding";

// Routes that must never show the overlay: onboarding renders the consent
// card inline, auth/oauth pages run before or during sign-in, and preview
// pages use fixture sessions that can't PATCH the real API.
const EXCLUDED_PREFIXES = ["/onboarding", "/login", "/oauth", "/preview"];

/**
 * Blocking usage-tracking consent overlay for users who are past onboarding
 * (or mid-flow on another page) without having accepted the current consent
 * version — including everyone onboarded before the gate existed and anyone
 * whose accepted version predates a UsageConsent.CurrentVersion bump.
 */
export function UsageConsentGate() {
  const pathname = usePathname();
  const { session } = useSessionContext();

  const excluded =
    !pathname || EXCLUDED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
  const authenticated = session.status === "authenticated";

  const onboardingState = useOnboardingStateQuery({ enabled: authenticated && !excluded });

  // Only ever block on a definitive "not accepted" — while loading or errored
  // (e.g. expired session mid-redirect) the app stays usable.
  const needsConsent =
    authenticated && !excluded && onboardingState.data?.usageConsentAccepted === false;

  if (!needsConsent) return null;

  return <UsageConsentOverlay />;
}

/** Presentation of the blocking overlay — separated so dev previews can render it directly. */
export function UsageConsentOverlay() {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-background/80 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Usage tracking consent"
    >
      <div className="my-auto w-full max-w-lg rounded-lg border border-border bg-card p-5 shadow-lg">
        <UsageConsentCard />
      </div>
    </div>
  );
}
